import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { handleApiError } from "../../pages/UBTAdmin/ProjectDetails/ProjectHelper/ViewProjectHandleApiError";

export interface TestOfChangeItem {
    [key: string]: any;
}

export interface TestsOfChangeState {
    testOfChangeList: TestOfChangeItem[];
    testOfChangeLoading: boolean;
    testOfChangeError: string | null;
    addTestOfChangeLoading: boolean;
    addTestOfChangeError: string | null;
    updateTestOfChangeLoading: boolean;
    updateTestOfChangeError: string | null;
    testOfChangeCurrentPage: number;
    testOfChangePerPage: number;
    testOfChangeTotalItems: number;
    testOfChangeTotalPages: number;
}

const initialState: TestsOfChangeState = {
    testOfChangeList: [],
    testOfChangeLoading: false,
    testOfChangeError: null,
    addTestOfChangeLoading: false,
    addTestOfChangeError: null,
    updateTestOfChangeLoading: false,
    updateTestOfChangeError: null,
    testOfChangeCurrentPage: 1,
    testOfChangePerPage: 5,
    testOfChangeTotalItems: 0,
    testOfChangeTotalPages: 0,
};

export const fetchTestsOfChange = createAsyncThunk(
    "ubtPortal/fetchTestsOfChange",
    async (
        {
            projectId,
            page,
            perPage,
            sortColumn,
            sortOrder,
            countOnly,
            nuid,
        }: {
            projectId: number;
            page?: number;
            perPage?: number;
            sortColumn?: string;
            sortOrder?: string;
            countOnly?: boolean;
            nuid?: string | null;
        },
        { rejectWithValue, signal },
    ) => {
        try {
            const params: any = {};
            if (page != null) params.page = page;
            if (perPage != null) params.per_page = perPage;
            if (sortColumn) params.sortColumn = sortColumn;
            if (sortOrder) params.sortOrder = sortOrder;
            if (countOnly != null) params.countOnly = countOnly;

            const options: any = {
                signal,
                params,
                headers: {
                    accept: "application/json",
                    ...(nuid ? { "x-nuid": nuid } : {}),
                },
            };

            const url =
                typeof endpoints.ubt.testsOfChange.getTestsOfChange === "function"
                    ? endpoints.ubt.testsOfChange.getTestsOfChange(projectId)
                    : endpoints.ubt.testsOfChange.getTestsOfChange;
            const response = await api.get(url, options);
            return response.data;
        } catch (error: any) {
            return handleApiError(error, rejectWithValue, "Failed to fetch tests of change");
        }
    },
);

export const addTestOfChange = createAsyncThunk(
    "ubtPortal/addTestOfChange",
    async (
        {
            projectId,
            payload,
            nuid,
        }: { projectId: number; payload: any; nuid?: string | null },
        { rejectWithValue },
    ) => {
        try {
            const options: any = {
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    ...(nuid ? { "x-nuid": nuid } : {}),
                },
            };

            const url =
                typeof endpoints.ubt.testsOfChange.addTestOfChange === "function"
                    ? endpoints.ubt.testsOfChange.addTestOfChange(projectId)
                    : endpoints.ubt.testsOfChange.addTestOfChange;
            const response = await api.post(url, payload, options);
            return response.data;
        } catch (error: any) {
            return handleApiError(error, rejectWithValue, "Failed to add test of change");
        }
    },
);

export const updateTestOfChange = createAsyncThunk(
    "ubtPortal/updateTestOfChange",
    async (
        {
            projectId,
            tocId,
            payload,
            nuid,
        }: { projectId: number; tocId: number; payload: any; nuid?: string | null },
        { rejectWithValue },
    ) => {
        try {
            const options: any = {
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    ...(nuid ? { "x-nuid": nuid } : {}),
                },
            };

            const url =
                typeof endpoints.ubt.testsOfChange.updateTestOfChange === "function"
                    ? endpoints.ubt.testsOfChange.updateTestOfChange(projectId, tocId)
                    : endpoints.ubt.testsOfChange.updateTestOfChange;
            const response = await api.patch(url, payload, options);
            return { tocId, data: response.data };
        } catch (error: any) {
            return handleApiError(error, rejectWithValue, "Failed to update test of change");
        }
    },
);

export const deleteTestOfChange = createAsyncThunk(
    "ubtPortal/deleteTestOfChange",
    async (
        { projectId, tocId, nuid }: { projectId: number; tocId: number; nuid?: string | null },
        { rejectWithValue },
    ) => {
        try {
            const options: any = {
                headers: {
                    accept: "application/json",
                    ...(nuid ? { "x-nuid": nuid } : {}),
                },
            };

            const url =
                typeof endpoints.ubt.testsOfChange.deleteTestOfChange === "function"
                    ? endpoints.ubt.testsOfChange.deleteTestOfChange(projectId, tocId)
                    : endpoints.ubt.testsOfChange.deleteTestOfChange;
            const response = await api.delete(url, options);
            return { tocId, data: response.data };
        } catch (error: any) {
            return handleApiError(error, rejectWithValue, "Failed to delete test of change");
        }
    },
);

const testsOfChangeSlice = createSlice({
    name: "testsOfChange",
    initialState,
    reducers: {
        resetTestsOfChangeState: (state) => {
            state.testOfChangeList = [];
            state.testOfChangeLoading = false;
            state.testOfChangeError = null;
            state.testOfChangeCurrentPage = 1;
            state.testOfChangeTotalItems = 0;
            state.testOfChangeTotalPages = 0;
            state.addTestOfChangeLoading = false;
            state.addTestOfChangeError = null;
            state.updateTestOfChangeLoading = false;
            state.updateTestOfChangeError = null;
        },
        setTestsOfChangeCurrentPage: (state, action: PayloadAction<number>) => {
            state.testOfChangeCurrentPage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTestsOfChange.pending, (state) => {
            state.testOfChangeLoading = true;
            state.testOfChangeError = null;
        });

        builder.addCase(fetchTestsOfChange.fulfilled, (state, action) => {
            state.testOfChangeLoading = false;
            const payload: any = action.payload;
            if (payload?.items && Array.isArray(payload.items)) {
                state.testOfChangeList = payload.items;
                state.testOfChangeTotalItems = payload.meta?.total ?? state.testOfChangeTotalItems;
                state.testOfChangeTotalPages = payload.meta?.totalPages ?? state.testOfChangeTotalPages;
                state.testOfChangeCurrentPage = payload.meta?.pageNumber ?? state.testOfChangeCurrentPage;
                state.testOfChangePerPage = payload.meta?.pageSize ?? state.testOfChangePerPage;
            } else if (Array.isArray(payload)) {
                state.testOfChangeList = payload;
                state.testOfChangeTotalItems = payload.length;
                state.testOfChangeTotalPages = 1;
                state.testOfChangeCurrentPage = 1;
            } else if (payload?.data && Array.isArray(payload.data)) {
                state.testOfChangeList = payload.data;
                state.testOfChangeTotalItems = payload.total ?? state.testOfChangeTotalItems;
            } else {
                state.testOfChangeList = [];
            }
        });

        builder.addCase(fetchTestsOfChange.rejected, (state, action) => {
            state.testOfChangeLoading = false;
            state.testOfChangeError = action.payload as string;
        });

        builder.addCase(addTestOfChange.pending, (state) => {
            state.addTestOfChangeLoading = true;
            state.addTestOfChangeError = null;
        });

        builder.addCase(addTestOfChange.fulfilled, (state, action) => {
            state.addTestOfChangeLoading = false;
            const payload: any = action.payload;
            const created = payload?.data ?? payload;

            if (created) {
                if (Array.isArray(created)) {
                    state.testOfChangeList = [...created, ...state.testOfChangeList];
                    state.testOfChangeTotalItems = (state.testOfChangeTotalItems ?? 0) + created.length;
                } else {
                    state.testOfChangeList = [created, ...state.testOfChangeList];
                    state.testOfChangeTotalItems = (state.testOfChangeTotalItems ?? 0) + 1;
                }
            }
        });

        builder.addCase(addTestOfChange.rejected, (state, action) => {
            state.addTestOfChangeLoading = false;
            state.addTestOfChangeError = action.payload as string;
        });
        
        builder.addCase(updateTestOfChange.pending, (state) => {
            state.updateTestOfChangeLoading = true;
            state.updateTestOfChangeError = null;
        });

        builder.addCase(updateTestOfChange.fulfilled, (state, action) => {
            state.updateTestOfChangeLoading = false;
            const payload: any = action.payload;
            const tocId = payload?.tocId ?? (payload?.data?.tocId ?? null);
            const updated = payload?.data ?? payload;

            if (tocId != null) {
                state.testOfChangeList = state.testOfChangeList.map((it: any) =>
                    (it.tocId ?? it.id) === tocId ? (updated?.data ?? updated) : it,
                );
            } else if (updated) {
                const matchId = updated.tocId ?? updated.id;
                if (matchId != null) {
                    state.testOfChangeList = state.testOfChangeList.map((it: any) =>
                        (it.tocId ?? it.id) === matchId ? updated : it,
                    );
                }
            }
        });

        builder.addCase(updateTestOfChange.rejected, (state, action) => {
            state.updateTestOfChangeLoading = false;
            state.updateTestOfChangeError = action.payload as string;
        });

        builder.addCase(deleteTestOfChange.pending, (state) => {
            // could set a loading flag if desired
            state.testOfChangeError = null;
        });

        builder.addCase(deleteTestOfChange.fulfilled, (state, action) => {
            const payload: any = action.payload;
            const tocId = payload?.tocId ?? null;
            if (tocId != null) {
                state.testOfChangeList = state.testOfChangeList.filter((it: any) => (it.tocId ?? it.id) !== tocId);
                state.testOfChangeTotalItems = Math.max(0, (state.testOfChangeTotalItems ?? 0) - 1);
            }
        });

        builder.addCase(deleteTestOfChange.rejected, (state, action) => {
            state.testOfChangeError = action.payload as string;
        });
    },
});

export const { resetTestsOfChangeState, setTestsOfChangeCurrentPage } = testsOfChangeSlice.actions;
export default testsOfChangeSlice.reducer;
